import { AdminOrganizationRow } from './admin-organization.model';

export class AdminOverviewRange {
  from!: string;
  to!: string;

  constructor(data: any = {}) {
    this.from = data.from;
    this.to = data.to;
  }
}

export class AdminPlatformStats {
  organizations!: number;
  billingExemptOrganizations!: number;
  focusOrganizations!: number;
  users!: number;
  superadmins!: number;
  activeApiKeys!: number;

  constructor(data: any = {}) {
    this.organizations = data.organizations;
    this.billingExemptOrganizations = data.billing_exempt_organizations;
    this.focusOrganizations = data.focus_organizations;
    this.users = data.users;
    this.superadmins = data.superadmins;
    this.activeApiKeys = data.active_api_keys;
  }
}

export class AdminUsageStats {
  requests!: number;
  errors!: number;
  credits!: number;
  costCents!: number;
  avgLatencyMs!: number;
  /** Keyed by source, e.g. { playground: 181, api: 1216 } shape isn't
   *  fixed to just those two in the contract, so kept as a plain record. */
  bySource: Record<string, number> = {};

  constructor(data: any = {}) {
    this.requests = data.requests;
    this.errors = data.errors;
    this.credits = data.credits;
    this.costCents = data.cost_cents;
    this.avgLatencyMs = data.avg_latency_ms;
    this.bySource = data.by_source ?? {};
  }

  get errorRate(): number {
    return this.requests > 0 ? this.errors / this.requests : 0;
  }
}

export class AdminWalletsStats {
  balanceCents!: number;
  negativeBalances!: number;
  currency!: string;

  constructor(data: any = {}) {
    this.balanceCents = data.balance_cents;
    this.negativeBalances = data.negative_balances;
    this.currency = data.currency;
  }
}

export interface CikisiClientRef {
  id: number;
  name: string;
}

/** organization here is the exact same shape as a row of GET /admin/organizations
 *  (id, name, slug, billing_exempt, focus, cikisi_client, balance_cents, currency,
 *  created_at) per the confirmed contract, so it's the same AdminOrganizationRow
 *  used everywhere else in the panel one model, not a slimmer overview-only copy. */
export class AdminTopOrganization {
  organization!: AdminOrganizationRow;
  requests!: number;
  credits!: number;
  costCents!: number;

  constructor(data: any = {}) {
    this.organization = new AdminOrganizationRow(data.organization);
    this.requests = data.requests;
    this.credits = data.credits;
    this.costCents = data.cost_cents;
  }
}

/** The `previous` block of GET /admin/overview: the same-length window
 *  ending at `from`, for the "vs before" comparisons. `top_organizations`
 *  entries here only carry an `organization.slug` (no full org row, no
 *  `credits`) they're matched against the current window's rows by slug,
 *  never displayed directly. */
export class AdminOverviewComparison {
  range!: AdminOverviewRange;
  usage!: AdminUsageStats;
  topOrganizations: AdminTopOrganization[] = [];

  constructor(data: any = {}) {
    this.range = new AdminOverviewRange(data.range);
    this.usage = new AdminUsageStats(data.usage);
    this.topOrganizations = (data.top_organizations ?? []).map((o: object) => new AdminTopOrganization(o));
  }
}

export class AdminOverviewResult {
  range!: AdminOverviewRange;
  platform!: AdminPlatformStats;
  usage!: AdminUsageStats;
  wallets!: AdminWalletsStats;
  /** Ranked by spend, falling back to volume server-side ordering, not re-sorted here. */
  topOrganizations: AdminTopOrganization[] = [];
  /** The window right before this one, for "vs before" comparisons null
   *  when the platform is younger than the window. */
  previous: AdminOverviewComparison | null = null;

  constructor(data: any = {}) {
    this.range = new AdminOverviewRange(data.range);
    this.platform = new AdminPlatformStats(data.platform);
    this.usage = new AdminUsageStats(data.usage);
    this.wallets = new AdminWalletsStats(data.wallets);
    this.topOrganizations = (data.top_organizations ?? []).map((o: object) => new AdminTopOrganization(o));
    this.previous = data.previous ? new AdminOverviewComparison(data.previous) : null;
  }
}
